/** First-sign-in guidance. Connected teachers always use the complete class directory. */
import { useState } from 'preact/hooks';
import { AddAppChoice, type BuildPath } from './builder.js';
import type { ServerClient, ConnectionStatus, ClassOverview, CoursesResult } from './api.js';

const DISMISS_KEY = 'tip.teacher.setup.dismissed';
export function setupDismissed(): boolean {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
}
export function dismissSetup(): void {
  try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* Presentation only. */ }
}
export function scopeSentence(scope: string): string {
  if (scope.endsWith('classroom.courses.readonly')) return 'see the courses you teach';
  if (scope.endsWith('classroom.coursework.students')) return 'create assignments and draft grades';
  return 'use an additional Google permission (review the description on Google’s next screen)';
}
export function needsSetup(connection: ConnectionStatus | null, classes: ClassOverview[] | null,
  _courses: CoursesResult | null, reconnect: boolean): boolean {
  return connection !== null && classes !== null && connection.connectable && !reconnect
    && connection.source !== 'own';
}

export function SetupFlow(props: {
  api: ServerClient; connection: ConnectionStatus; classes: ClassOverview[]; courses: CoursesResult | null;
  onFinish(path?: BuildPath): void; onLinked(): void;
}) {
  const [skipped, setSkipped] = useState(false);
  const connected = props.connection.source === 'own';
  return <section class="setup" aria-labelledby="setup-title">
    <p class="setup-kicker">Getting started</p>
    <h2 class="section-title" id="setup-title">Set up TIP for your classes</h2>
    <p class="lede">Connect Google Classroom to see the active classes you teach. You can also start an app and connect later.</p>
    <p><button type="button" class="linkish" onClick={() => props.onFinish()}>View my classes instead</button></p>
    <ol class="setup-steps">
      <li class="setup-step" data-state={connected ? 'done' : skipped ? 'skipped' : 'current'}>
        <span class="mark" aria-hidden="true">{connected ? '✓' : skipped ? '–' : '1'}</span>
        <div class="body"><h3>Connect Google Classroom</h3>
          {connected ? <p class="state">Connected{props.connection.account ? <> as <code>{props.connection.account}</code></> : null}.</p>
            : skipped ? <p class="state">Skipped. Connect any time from Your classes.</p>
            : <><p>TIP can see the classes you teach and send grades when you choose. Grades arrive as drafts (not yet shown to students). You still return them in Google Classroom.</p>
              {props.connection.scopes.length ? <p class="fine">Google will ask you to allow TIP to: {props.connection.scopes.map(scopeSentence).join('; ')}.</p> : null}
              <div class="setup-act"><a class="act" href={props.api.connectStartUrl()} style="text-decoration:none;display:inline-block;">Connect Google Classroom</a>
                <button type="button" class="linkish" onClick={() => setSkipped(true)}>Skip for now</button></div></>}
        </div>
      </li>
      <li class="setup-step" data-state={connected || skipped ? 'current' : 'next'}>
        <span class="mark" aria-hidden="true">2</span>
        <div class="body"><h3>Add your first app</h3>
          {connected || skipped ? <><AddAppChoice compact onChoose={p => props.onFinish(p)} />
            <p class="setup-done-link"><button type="button" class="linkish" onClick={() => props.onFinish()}>Go to my classes instead →</button></p></>
            : <p class="state">Pick a ready-made game or describe your own.</p>}
        </div>
      </li>
    </ol>
  </section>;
}
